/**
 * FikoPDF Template - Veri Modelleri
 * PDF şablon editörü için interface tanımları
 *
 * @author FikoYT
 * @version 1.0.0
 */

// ============ SAYFA AYARLARI ============

// ISO A Serisi + B Serisi + Kuzey Amerika + Etiket Boyutları
export type PageSize =
  // ISO A Serisi
  | 'A0' | 'A1' | 'A2' | 'A3' | 'A4' | 'A5' | 'A6' | 'A7' | 'A8'
  // ISO B Serisi
  | 'B0' | 'B1' | 'B2' | 'B3' | 'B4' | 'B5' | 'B6'
  // Kuzey Amerika
  | 'Letter' | 'Legal' | 'Tabloid' | 'Ledger' | 'Executive'
  // Etiket Boyutları
  | 'Label-Small' | 'Label-Medium' | 'Label-Large' | 'Label-XL'
  // Özel
  | 'Custom';

export type PageOrientation = 'portrait' | 'landscape';

export interface PageSettings {
  size: PageSize;
  orientation: PageOrientation;
  width: number;   // mm
  height: number;  // mm
  margins: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  backgroundColor?: string;
}

// Standart sayfa boyutları (mm)
export const PAGE_SIZES: Record<PageSize, { width: number; height: number }> = {
  // ═══════════════ ISO A SERİSİ ═══════════════
  'A0': { width: 841, height: 1189 },   // Poster, büyük teknik çizim
  'A1': { width: 594, height: 841 },    // Teknik çizim, poster
  'A2': { width: 420, height: 594 },    // Poster, diyagram
  'A3': { width: 297, height: 420 },    // Büyük doküman, çizim
  'A4': { width: 210, height: 297 },    // Standart doküman
  'A5': { width: 148, height: 210 },    // Kitapçık, el ilanı
  'A6': { width: 105, height: 148 },    // Kartpostal, not
  'A7': { width: 74, height: 105 },     // Cep kartı
  'A8': { width: 52, height: 74 },      // Küçük etiket

  // ═══════════════ ISO B SERİSİ ═══════════════
  'B0': { width: 1000, height: 1414 },  // Büyük poster
  'B1': { width: 707, height: 1000 },   // Poster
  'B2': { width: 500, height: 707 },    // Poster
  'B3': { width: 353, height: 500 },    // Büyük kitap
  'B4': { width: 250, height: 353 },    // Kitap, dergi
  'B5': { width: 176, height: 250 },    // Kitap
  'B6': { width: 125, height: 176 },    // Küçük kitap

  // ═══════════════ KUZEY AMERİKA ═══════════════
  'Letter': { width: 216, height: 279 },     // US Letter (8.5 x 11 inch)
  'Legal': { width: 216, height: 356 },      // US Legal (8.5 x 14 inch)
  'Tabloid': { width: 279, height: 432 },    // Tabloid (11 x 17 inch)
  'Ledger': { width: 432, height: 279 },     // Ledger (17 x 11 inch) - Tabloid yatay
  'Executive': { width: 184, height: 267 },  // Executive (7.25 x 10.5 inch)

  // ═══════════════ ETİKET BOYUTLARI ═══════════════
  'Label-Small': { width: 50, height: 25 },   // Küçük barkod etiketi
  'Label-Medium': { width: 70, height: 35 },  // Orta etiket
  'Label-Large': { width: 100, height: 50 },  // Büyük etiket
  'Label-XL': { width: 150, height: 100 },    // Kargo etiketi

  // ═══════════════ ÖZEL ═══════════════
  'Custom': { width: 210, height: 297 }       // Özel boyut (varsayılan A4)
};

// ============ ELEMENT TİPLERİ ============

export type PdfElementType =
  | 'screenshot'    // 3D viewport görüntüsü
  | 'text'          // Metin (başlık, paragraf)
  | 'image'         // Yüklenen resim (logo vb)
  | 'table'         // Tablo
  | 'measurement'   // Ölçüm listesi
  | 'auto-data'     // Otomatik veri (tarih, dosya adı, boyutlar)
  | 'shape'         // Çizgi, dikdörtgen, daire
  | 'qrcode';       // QR kod (link, proje ID)

// ============ ŞEKİL TİPLERİ ============

export type ShapeType =
  // ═══════════════ TEMEL ŞEKİLLER ═══════════════
  | 'line'              // Düz çizgi
  | 'rect'              // Dikdörtgen
  | 'rounded-rect'      // Yuvarlatılmış dikdörtgen
  | 'circle'            // Daire
  | 'ellipse'           // Elips
  | 'semicircle'        // Yarım daire
  | 'quarter-circle'    // Çeyrek daire

  // ═══════════════ ÇOKGENLER ═══════════════
  | 'triangle'          // Üçgen
  | 'right-triangle'    // Dik üçgen
  | 'diamond'           // Eşkenar dörtgen (baklava)
  | 'parallelogram'     // Paralelkenar
  | 'trapezoid'         // Yamuk
  | 'pentagon'          // Beşgen
  | 'hexagon'           // Altıgen
  | 'octagon'           // Sekizgen

  // ═══════════════ OKLAR ═══════════════
  | 'arrow'             // Düz ok
  | 'double-arrow'      // Çift yönlü ok
  | 'curved-arrow'      // Eğri ok
  | 'chevron'           // Şerit ok (chevron)
  | 'block-arrow'       // Blok ok (kalın)
  | 'bent-arrow'        // Kırık ok (90°)
  | 'circular-arrow'    // Döngüsel ok

  // ═══════════════ ENDÜSTRİYEL SİMGELER ═══════════════
  | 'gear'              // Dişli çark
  | 'gear-double'       // Çift dişli
  | 'bearing'           // Rulman/yatak
  | 'spring'            // Yay
  | 'bolt'              // Cıvata
  | 'nut'               // Somun
  | 'piston'            // Piston

  // ═══════════════ TEKNİK ÇİZİM ═══════════════
  | 'centerline'        // Merkez çizgisi
  | 'dimension-line'    // Ölçü çizgisi
  | 'section-marker'    // Kesit işareti
  | 'datum'             // Referans (datum)
  | 'surface-finish'    // Yüzey kalitesi

  // ═══════════════ UYARI/GÜVENLİK ═══════════════
  | 'warning-triangle'  // Uyarı üçgeni
  | 'danger-sign'       // Tehlike işareti
  | 'prohibition'       // Yasak işareti
  | 'info-circle'       // Bilgi dairesi
  | 'mandatory'         // Zorunlu işaret

  // ═══════════════ KALİTE KONTROL ═══════════════
  | 'checkmark'         // Onay işareti ✓
  | 'x-mark'            // Red işareti ✗
  | 'cross'             // Artı işareti +
  | 'quality-stamp'     // Kalite damgası
  | 'inspection-mark'   // Denetim işareti

  // ═══════════════ AKIŞ DİYAGRAMI ═══════════════
  | 'process-box'       // İşlem kutusu
  | 'decision'          // Karar (diamond)
  | 'connector'         // Bağlayıcı
  | 'document'          // Belge
  | 'database'          // Veritabanı
  | 'terminator'        // Başlat/Bitir

  // ═══════════════ DİĞER SİMGELER ═══════════════
  | 'star'              // Yıldız
  | 'heart'             // Kalp
  | 'lightning'         // Şimşek
  | 'cloud'             // Bulut
  | 'speech-bubble'     // Konuşma balonu
  | 'tag'               // Etiket
  | 'flag'              // Bayrak
  | 'pin'               // Pin/İşaretçi

  // ═══════════════ BAĞLANTILAR ═══════════════
  | 'bracket-left'      // Sol köşeli ayraç [
  | 'bracket-right'     // Sağ köşeli ayraç ]
  | 'brace-left'        // Sol süslü ayraç {
  | 'brace-right'       // Sağ süslü ayraç }
  | 'angle-bracket-l'   // Sol açılı ayraç <
  | 'angle-bracket-r';  // Sağ açılı ayraç >

// ============ ELEMENT INTERFACE ============

export interface PdfElement {
  id: string;
  type: PdfElementType;

  // Pozisyon ve boyut (mm cinsinden)
  x: number;
  y: number;
  width: number;
  height: number;

  // Transform
  rotation: number;   // derece
  zIndex: number;

  // Durum
  locked: boolean;
  visible: boolean;
  selected?: boolean;

  // Tip-spesifik özellikler
  properties: ElementProperties;
}

// ============ ELEMENT ÖZELLİKLERİ ============

export interface ElementProperties {
  // === GENEL STİLLER ===
  opacity?: number;            // 0-1 arası
  borderRadius?: number;       // px
  backgroundColor?: string;
  padding?: number;

  // Border (tüm elementler için)
  borderWidth?: number;
  borderColor?: string;
  borderStyle?: 'none' | 'solid' | 'dashed' | 'dotted';

  // Gölge
  shadowEnabled?: boolean;
  shadowOffsetX?: number;
  shadowOffsetY?: number;
  shadowBlur?: number;
  shadowColor?: string;

  // === TEXT ===
  content?: string;
  fontSize?: number;           // pt
  fontFamily?: string;
  fontWeight?: 'normal' | 'bold';
  fontStyle?: 'normal' | 'italic';
  textDecoration?: 'none' | 'underline' | 'line-through';
  fontColor?: string;
  textAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  lineHeight?: number;         // örn: 1.5
  letterSpacing?: number;      // px

  // === IMAGE / SCREENSHOT ===
  src?: string;                // base64 veya URL
  objectFit?: 'contain' | 'cover' | 'fill';
  screenshotScale?: 1 | 2 | 4;
  screenshotView?: 'current' | 'front' | 'back' | 'left' | 'right' | 'top' | 'bottom' | 'iso';
  transparent?: boolean;       // Transparan arka plan
  border?: {
    width: number;
    color: string;
    style: 'solid' | 'dashed';
  };

  // === KIRPMA (CROP) - Word tarzı ===
  cropTop?: number;            // % olarak üstten kırpma (0-50)
  cropRight?: number;          // % olarak sağdan kırpma (0-50)
  cropBottom?: number;         // % olarak alttan kırpma (0-50)
  cropLeft?: number;           // % olarak soldan kırpma (0-50)
  cropMode?: boolean;          // Kırpma modu aktif mi?

  // === TABLE ===
  rows?: number;
  cols?: number;
  data?: string[][];
  headerRow?: boolean;
  headerBgColor?: string;
  cellPadding?: number;

  // === AUTO-DATA ===
  dataSource?: AutoDataSource;
  format?: string;             // örn: "dd.MM.yyyy" tarih için
  prefix?: string;
  suffix?: string;
  sourceObjectId?: string;     // Veri kaynağı nesne ID'si (seçimli)
  dimensionSort?: boolean;     // true: Büyük × Orta × Küçük sıralaması

  // === SHAPE ===
  shapeType?: ShapeType;
  strokeColor?: string;
  strokeWidth?: number;
  fillColor?: string;
  lineDash?: number[];
  lineStyle?: 'solid' | 'dashed' | 'dotted';
  cornerRadius?: number;      // rounded-rect için köşe yuvarlaklığı
  arrowHeadSize?: number;     // Ok başı boyutu
  arrowStyle?: 'simple' | 'filled' | 'double';  // Ok stili
  starPoints?: number;        // Yıldız köşe sayısı (5, 6, 8, vb.)
  starInnerRadius?: number;   // Yıldız iç yarıçap oranı (0-1)

  // === QRCODE ===
  qrContent?: string;
  qrSize?: number;
}

// Otomatik veri kaynakları
export type AutoDataSource =
  | 'date'           // Bugünün tarihi
  | 'datetime'       // Tarih ve saat
  | 'filename'       // Model dosya adı
  | 'dimensions'     // Boyutlar (X × Y × Z)
  | 'volume'         // Hacim
  | 'surface-area'   // Yüzey alanı
  | 'weight'         // Ağırlık
  | 'material'       // Malzeme
  | 'center-of-mass' // Kütle merkezi
  | 'measurement-count' // Ölçüm sayısı
  | 'project-name'   // Proje adı
  | 'user-name';     // Kullanıcı adı

// ============ PARAMETRE SİSTEMİ (Delphi Tarzı) ============

/**
 * Parametre kaynak tipi
 * - manual: Kullanıcı tarafından elle girilen değer
 * - cizim-linked: 3D çizim nesnesinden otomatik çekilen veri
 * - system: Sistem tarafından otomatik oluşturulan (tarih, kullanıcı vb.)
 * - calculated: Hesaplanan değer (formül)
 */
export type PdfParameterSource = 'manual' | 'cizim-linked' | 'system' | 'calculated';

/**
 * Parametre veri tipi
 */
export type PdfParameterDataType = 'string' | 'number' | 'date' | 'boolean' | 'list';

/**
 * Çizim bağlantılı parametre için veri alanları
 */
export type CizimDataField =
  | 'name'           // Çizim adı
  | 'dimension-x'    // X boyutu
  | 'dimension-y'    // Y boyutu
  | 'dimension-z'    // Z boyutu
  | 'dimensions'     // Tüm boyutlar (X × Y × Z)
  | 'volume'         // Hacim
  | 'surface-area'   // Yüzey alanı
  | 'weight'         // Ağırlık
  | 'material'       // Malzeme
  | 'density';       // Özkütle

/**
 * Sistem parametreleri için veri alanları
 */
export type SystemDataField =
  | 'current-date'       // Bugünün tarihi
  | 'current-datetime'   // Tarih ve saat
  | 'current-time'       // Sadece saat
  | 'user-name'          // Kullanıcı adı
  | 'project-name'       // Proje adı
  | 'company-name'       // Şirket adı
  | 'page-number'        // Sayfa numarası
  | 'total-pages'        // Toplam sayfa
  | 'revision'           // Revizyon numarası
  | 'template-name';     // Şablon adı

/**
 * PDF Şablon Parametresi
 * Delphi tarzı küçük kare marker olarak görünür, PDF'te gözükmez
 * Tüm elementlerde {{parametre_adi}} şeklinde kullanılabilir
 */
export interface PdfParameter {
  id: string;
  name: string;                    // Parametre adı (benzersiz, {{name}} şeklinde kullanılır)
  label: string;                   // Görünen etiket (Türkçe)
  description?: string;            // Açıklama

  // Kaynak ve tip
  source: PdfParameterSource;
  dataType: PdfParameterDataType;

  // Değer
  value?: string | number | boolean | Date;   // Manuel veya hesaplanmış değer
  defaultValue?: string | number | boolean;   // Varsayılan değer

  // Çizim bağlantısı (source === 'cizim-linked' ise)
  linkedCizimId?: string;          // Bağlı çizim ID'si
  linkedCizimField?: CizimDataField; // Hangi alan

  // Sistem parametresi (source === 'system' ise)
  systemField?: SystemDataField;

  // Hesaplanan değer (source === 'calculated' ise)
  formula?: string;                // Formül (örn: "{{param1}} * 2 + {{param2}}")

  // Görünüm
  visible: boolean;                // Parametre panelinde görünsün mü?
  required: boolean;               // Zorunlu mu?
  readOnly: boolean;               // Salt okunur mu?

  // Biçimlendirme
  format?: string;                 // Tarih/sayı formatı (örn: "dd.MM.yyyy", "#,##0.00")
  prefix?: string;                 // Ön ek
  suffix?: string;                 // Son ek
  unit?: string;                   // Birim (mm, kg, vb.)

  // Liste tipi için
  options?: { value: string; label: string }[];  // Seçenekler

  // Marker pozisyonu (editörde görünür, PDF'te gizli)
  markerX?: number;                // Marker X pozisyonu (mm)
  markerY?: number;                // Marker Y pozisyonu (mm)
  markerColor?: string;            // Marker rengi
}

/**
 * Parametre grubu (organize etmek için)
 */
export interface PdfParameterGroup {
  id: string;
  name: string;
  expanded: boolean;
  parameters: string[];            // Parametre ID'leri
}

// ============ ŞABLON ============

export interface PdfTemplate {
  id: string;
  name: string;
  description?: string;
  thumbnail?: string;          // base64 önizleme

  // Sayfa ayarları
  pageSettings: PageSettings;

  // Elementler
  elements: PdfElement[];

  // Parametreler (Delphi tarzı)
  parameters?: PdfParameter[];
  parameterGroups?: PdfParameterGroup[];

  // Meta
  createdAt: string;           // ISO date
  updatedAt: string;
  version: number;
  isBuiltIn?: boolean;         // Sistem şablonu mu?
}

// ============ EDİTÖR STATE ============

export interface EditorState {
  template: PdfTemplate;
  selectedElementId: string | null;
  clipboard: PdfElement | null;
  history: PdfTemplate[];
  historyIndex: number;
  zoom: number;
  showGrid: boolean;
  snapToGrid: boolean;
  gridSize: number;            // mm
}

// ============ KANAL MESAJLARI ============

export type ChannelMessageType =
  | 'ping'
  | 'pong'
  | 'request-screenshot'
  | 'screenshot-result'
  | 'request-measurements'
  | 'measurements-result'
  | 'request-model-info'
  | 'model-info-result'
  | 'request-object-list'
  | 'object-list-result'
  | 'request-object-info'
  | 'object-info-result'
  | 'request-cizim-list'
  | 'cizim-list-result'
  | 'start-selection-mode'    // PDF editör -> 3D: Nesne seçim modunu başlat
  | 'cancel-selection-mode'   // PDF editör -> 3D: Seçim modunu iptal et
  | 'selection-result'        // 3D -> PDF editör: Seçilen nesne bilgisi
  | 'focus-viewer'            // PDF editör -> 3D: 3D pencereyi öne getir
  | 'focus-editor'            // 3D -> PDF editör: PDF editörü öne getir
  | 'editor-ready'
  | 'editor-closed';

export interface ChannelMessage {
  type: ChannelMessageType;
  payload?: any;
  requestId?: string;
}

// ============ SCREENSHOT İSTEĞİ ============

export interface ScreenshotRequest {
  view: 'current' | 'front' | 'back' | 'left' | 'right' | 'top' | 'bottom' | 'iso';
  scale: 1 | 2 | 4;
  transparent: boolean;
  format: 'png' | 'jpeg';
}

export interface ScreenshotResult {
  dataUrl: string;
  width: number;
  height: number;
}

// ============ SAHNE NESNELERİ ============

export interface SceneObjectInfo {
  id: string;
  name: string;
  type: string;  // 'FikoCizimYalin', 'Mesh', 'Group' vb.
  hasVolumeData: boolean;
  hasWeightData: boolean;
}

export interface CizimInfo {
  id: string;
  name: string;
  type: string;
  visible: boolean;
  dimensions?: { x: number; y: number; z: number };
  volume?: number;
  surfaceArea?: number;
  weight?: number;
  material?: string;
  density?: number;
}

// ============ MODEL BİLGİSİ ============

export interface ModelInfo {
  id?: string;  // Nesne ID'si (seçimli nesne desteği için)
  filename: string;
  dimensions: { x: number; y: number; z: number };
  volume?: number;
  surfaceArea?: number;
  weight?: number;
  material?: string;
  centerOfMass?: { x: number; y: number; z: number };
  triangleCount?: number;
  density?: number;  // Özkütle (g/cm³)
}

// ============ ÖLÇÜMLER ============

export interface MeasurementItem {
  id: string;
  type: 'distance' | 'angle' | 'radius' | 'area';
  value: number;
  unit: string;
  label?: string;
  points?: { x: number; y: number; z: number }[];
}

// ============ FACTORY FUNCTIONS ============

export function createDefaultPageSettings(): PageSettings {
  return {
    size: 'A4',
    orientation: 'portrait',
    width: 210,
    height: 297,
    margins: { top: 10, bottom: 10, left: 10, right: 10 },
    backgroundColor: '#ffffff'
  };
}

export function createDefaultTemplate(): PdfTemplate {
  return {
    id: generateId(),
    name: 'Yeni Şablon',
    pageSettings: createDefaultPageSettings(),
    elements: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    version: 1
  };
}

export function createElement(type: PdfElementType, x: number, y: number): PdfElement {
  const defaults: Record<PdfElementType, Partial<PdfElement>> = {
    'screenshot': { width: 100, height: 75, properties: { screenshotScale: 2, screenshotView: 'current' } },
    'text': { width: 80, height: 10, properties: { content: 'Metin', fontSize: 12, fontColor: '#000000', textAlign: 'left' } },
    'image': { width: 50, height: 50, properties: { objectFit: 'contain' } },
    'table': { width: 100, height: 50, properties: { rows: 3, cols: 3, headerRow: true, data: [[]] } },
    'measurement': { width: 100, height: 60, properties: {} },
    'auto-data': { width: 60, height: 8, properties: { dataSource: 'date', fontSize: 10 } },
    'shape': { width: 50, height: 50, properties: { shapeType: 'rect', strokeColor: '#000000', strokeWidth: 1 } },
    'qrcode': { width: 30, height: 30, properties: { qrContent: 'https://fikoyt.com', qrSize: 128 } }
  };

  const def = defaults[type];

  return {
    id: generateId(),
    type,
    x,
    y,
    width: def.width || 50,
    height: def.height || 50,
    rotation: 0,
    zIndex: 0,
    locked: false,
    visible: true,
    properties: def.properties || {}
  };
}

function generateId(): string {
  return 'el_' + Math.random().toString(36).substring(2, 11);
}

// ============ PARAMETRE FACTORY FONKSİYONLARI ============

/**
 * Yeni manuel parametre oluştur
 */
export function createManualParameter(
  name: string,
  label: string,
  dataType: PdfParameterDataType = 'string',
  defaultValue?: string | number | boolean
): PdfParameter {
  return {
    id: 'param_' + Math.random().toString(36).substring(2, 11),
    name,
    label,
    source: 'manual',
    dataType,
    value: defaultValue,
    defaultValue,
    visible: true,
    required: false,
    readOnly: false
  };
}

/**
 * Çizim bağlantılı parametre oluştur
 */
export function createCizimLinkedParameter(
  name: string,
  label: string,
  linkedCizimField: CizimDataField,
  linkedCizimId?: string
): PdfParameter {
  return {
    id: 'param_' + Math.random().toString(36).substring(2, 11),
    name,
    label,
    source: 'cizim-linked',
    dataType: linkedCizimField === 'name' || linkedCizimField === 'material' ? 'string' : 'number',
    linkedCizimId,
    linkedCizimField,
    visible: true,
    required: false,
    readOnly: true
  };
}

/**
 * Sistem parametresi oluştur
 */
export function createSystemParameter(
  name: string,
  label: string,
  systemField: SystemDataField
): PdfParameter {
  const dateFields: SystemDataField[] = ['current-date', 'current-datetime', 'current-time'];
  const numberFields: SystemDataField[] = ['page-number', 'total-pages', 'revision'];

  let dataType: PdfParameterDataType = 'string';
  if (dateFields.includes(systemField)) dataType = 'date';
  if (numberFields.includes(systemField)) dataType = 'number';

  return {
    id: 'param_' + Math.random().toString(36).substring(2, 11),
    name,
    label,
    source: 'system',
    dataType,
    systemField,
    visible: true,
    required: false,
    readOnly: true
  };
}

/**
 * Hesaplanan parametre oluştur
 */
export function createCalculatedParameter(
  name: string,
  label: string,
  formula: string
): PdfParameter {
  return {
    id: 'param_' + Math.random().toString(36).substring(2, 11),
    name,
    label,
    source: 'calculated',
    dataType: 'number',
    formula,
    visible: true,
    required: false,
    readOnly: true
  };
}

/**
 * Parametre değerini çözümle
 * @param param Parametre
 * @param context Çözümleme bağlamı (çizim verileri, sistem bilgileri vb.)
 * @returns Çözümlenmiş değer string olarak
 */
export function resolveParameterValue(
  param: PdfParameter,
  context: ParameterResolveContext
): string {
  let value: any = param.value ?? param.defaultValue ?? '';

  switch (param.source) {
    case 'manual':
      // Manuel değer zaten set edilmiş
      break;

    case 'cizim-linked':
      if (param.linkedCizimId && param.linkedCizimField && context.cizimler) {
        const cizim = context.cizimler.find(c => c.id === param.linkedCizimId);
        if (cizim) {
          value = getCizimFieldValue(cizim, param.linkedCizimField, param.format);
        }
      }
      break;

    case 'system':
      if (param.systemField) {
        value = getSystemFieldValue(param.systemField, context, param.format);
      }
      break;

    case 'calculated':
      if (param.formula && context.parameters) {
        value = evaluateFormula(param.formula, context.parameters, context);
      }
      break;
  }

  // Biçimlendirme
  let result = formatValue(value, param);

  // Prefix ve suffix
  if (param.prefix) result = param.prefix + result;
  if (param.suffix) result = result + param.suffix;
  if (param.unit) result = result + ' ' + param.unit;

  return result;
}

/**
 * Parametre çözümleme bağlamı
 */
export interface ParameterResolveContext {
  cizimler?: CizimInfo[];
  modelInfo?: ModelInfo;
  parameters?: PdfParameter[];
  userName?: string;
  projectName?: string;
  companyName?: string;
  pageNumber?: number;
  totalPages?: number;
  revision?: number;
}

/**
 * Çizim alanından değer al
 */
function getCizimFieldValue(cizim: CizimInfo, field: CizimDataField, format?: string): any {
  switch (field) {
    case 'name': return cizim.name;
    case 'dimension-x': return cizim.dimensions?.x ?? 0;
    case 'dimension-y': return cizim.dimensions?.y ?? 0;
    case 'dimension-z': return cizim.dimensions?.z ?? 0;
    case 'dimensions':
      if (cizim.dimensions) {
        const dims = [cizim.dimensions.x, cizim.dimensions.y, cizim.dimensions.z].sort((a, b) => b - a);
        return dims.map(d => d.toFixed(2)).join(' × ');
      }
      return '';
    case 'volume': return cizim.volume ?? 0;
    case 'surface-area': return cizim.surfaceArea ?? 0;
    case 'weight': return cizim.weight ?? 0;
    case 'material': return cizim.material ?? '';
    case 'density': return cizim.density ?? 0;
    default: return '';
  }
}

/**
 * Sistem alanından değer al
 */
function getSystemFieldValue(field: SystemDataField, context: ParameterResolveContext, format?: string): any {
  const now = new Date();

  switch (field) {
    case 'current-date':
      return formatDate(now, format || 'dd.MM.yyyy');
    case 'current-datetime':
      return formatDate(now, format || 'dd.MM.yyyy HH:mm');
    case 'current-time':
      return formatDate(now, format || 'HH:mm:ss');
    case 'user-name':
      return context.userName ?? '';
    case 'project-name':
      return context.projectName ?? '';
    case 'company-name':
      return context.companyName ?? '';
    case 'page-number':
      return context.pageNumber ?? 1;
    case 'total-pages':
      return context.totalPages ?? 1;
    case 'revision':
      return context.revision ?? 1;
    case 'template-name':
      return '';
    default:
      return '';
  }
}

/**
 * Basit tarih formatla
 */
function formatDate(date: Date, format: string): string {
  const pad = (n: number) => n.toString().padStart(2, '0');

  return format
    .replace('yyyy', date.getFullYear().toString())
    .replace('MM', pad(date.getMonth() + 1))
    .replace('dd', pad(date.getDate()))
    .replace('HH', pad(date.getHours()))
    .replace('mm', pad(date.getMinutes()))
    .replace('ss', pad(date.getSeconds()));
}

/**
 * Değeri biçimlendir
 */
function formatValue(value: any, param: PdfParameter): string {
  if (value === null || value === undefined) return '';

  if (param.dataType === 'number' && typeof value === 'number') {
    if (param.format) {
      // Basit sayı formatı (#,##0.00 gibi)
      const decimals = (param.format.match(/0+$/) || [''])[0].length;
      return value.toLocaleString('tr-TR', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
    }
    return value.toString();
  }

  if (param.dataType === 'date' && value instanceof Date) {
    return formatDate(value, param.format || 'dd.MM.yyyy');
  }

  if (param.dataType === 'boolean') {
    return value ? 'Evet' : 'Hayır';
  }

  return String(value);
}

/**
 * Basit formül değerlendir
 * Desteklenen operatörler: +, -, *, /, (, )
 * Parametre referansları: {{param_name}}
 */
function evaluateFormula(
  formula: string,
  parameters: PdfParameter[],
  context: ParameterResolveContext
): number {
  try {
    // Parametre referanslarını değerlerle değiştir
    let expression = formula;
    const paramPattern = /\{\{(\w+)\}\}/g;

    expression = expression.replace(paramPattern, (match, paramName) => {
      const param = parameters.find(p => p.name === paramName);
      if (param) {
        const value = resolveParameterValue(param, { ...context, parameters: [] }); // Sonsuz döngü önleme
        const numValue = parseFloat(value);
        return isNaN(numValue) ? '0' : numValue.toString();
      }
      return '0';
    });

    // Güvenli değerlendirme (sadece matematik operatörleri)
    // Tehlikeli karakterleri kontrol et
    if (/[^0-9+\-*/().\s]/.test(expression)) {
      console.warn('Geçersiz formül karakteri:', expression);
      return 0;
    }

    // eval yerine Function kullan (biraz daha güvenli)
    const result = new Function('return ' + expression)();
    return typeof result === 'number' && !isNaN(result) ? result : 0;
  } catch (e) {
    console.error('Formül değerlendirme hatası:', e);
    return 0;
  }
}

/**
 * Metin içindeki parametre referanslarını çözümle
 * Örn: "Müşteri: {{musteri_adi}}" -> "Müşteri: Ahmet Yılmaz"
 */
export function resolveTextWithParameters(
  text: string,
  parameters: PdfParameter[],
  context: ParameterResolveContext
): string {
  if (!text || !parameters?.length) return text;

  const paramPattern = /\{\{(\w+)\}\}/g;

  return text.replace(paramPattern, (match, paramName) => {
    const param = parameters.find(p => p.name === paramName);
    if (param) {
      return resolveParameterValue(param, { ...context, parameters });
    }
    return match; // Bulunamazsa olduğu gibi bırak
  });
}
